import React, { useState, useEffect } from 'react';
import { api } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Mail, 
  Phone, 
  Briefcase, 
  Award,
  CheckCircle2,
  TrendingUp,
  Calendar,
  FileText
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const TeamMemberDetail = ({ member, onBack }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [tasks, setTasks] = useState([]);
  const [stats, setStats] = useState({
    tasksCompleted: 0,
    performanceRating: 0,
    attendanceRate: 0,
    openTasks: 0,
    inProgressTasks: 0,
    completedTasks: 0
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchMemberData();
  }, [member.id]);

  const fetchMemberData = async () => {
    try {
      setLoading(true);
      // Fetch tasks assigned to this member
      const tasksRes = await api.get('/ops/tasks');
      const allTasks = tasksRes.data || [];
      const memberTasks = allTasks.filter(task => 
        task.assigned_to && task.assigned_to.includes(member.id)
      );
      
      setTasks(memberTasks);
      
      // Calculate stats
      const completedTasks = memberTasks.filter(t => t.status === 'completed').length;
      const openTasks = memberTasks.filter(t => t.status === 'unassigned').length;
      const inProgressTasks = memberTasks.filter(t => t.status === 'in_progress').length;
      
      setStats({
        tasksCompleted: completedTasks,
        performanceRating: 4.7, // Mock data
        attendanceRate: 95, // Mock data
        openTasks,
        inProgressTasks,
        completedTasks
      });
    } catch (error) {
      console.error('Error fetching member data:', error);
      toast.error('Failed to load member details');
    } finally {
      setLoading(false);
    }
  };

  const totalTasks = stats.openTasks + stats.inProgressTasks + stats.completedTasks;
  const workloadPercentage = totalTasks > 0 ? Math.round((stats.inProgressTasks / totalTasks) * 100) : 0;

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <Button variant="ghost" onClick={onBack} className="text-slate-600 hover:text-slate-900">
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Team
      </Button>

      {/* Profile Card */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-6">
          <div className="flex items-start gap-6">
            {/* Avatar */}
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[#247C75] to-[#1d6259] flex items-center justify-center">
              <span className="text-3xl font-bold text-white">
                {member.name.split(' ').map(n => n[0]).join('').toUpperCase()}
              </span>
            </div>

            {/* Info */}
            <div className="flex-1">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-slate-900">{member.name}</h2>
                  <p className="text-slate-600 text-sm mt-1">{member.role}</p>
                </div>
                <Badge 
                  variant={member.status === 'active' ? 'default' : 'secondary'}
                  className={member.status === 'active' ? 'bg-green-100 text-green-700 border-green-300' : ''}
                >
                  {member.status === 'active' ? 'Available' : 'Unavailable'}
                </Badge>
              </div>

              <div className="grid grid-cols-2 gap-4 mt-4">
                {member.email && (
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="h-4 w-4 text-slate-500" />
                    <span className="text-slate-700">{member.email}</span>
                  </div>
                )}
                {member.phone && (
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="h-4 w-4 text-slate-500" />
                    <span className="text-slate-700">{member.phone}</span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-sm">
                  <Briefcase className="h-4 w-4 text-slate-500" />
                  <span className="text-slate-700 capitalize">{member.role}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Award className="h-4 w-4 text-slate-500" />
                  <span className="text-slate-700">{member.status}</span>
                </div>
              </div>

              {/* Skills & Expertise */}
              {member.skills && member.skills.length > 0 && (
                <div className="mt-4">
                  <h3 className="text-sm font-semibold text-slate-900 mb-2">Skills & Expertise</h3>
                  <div className="flex flex-wrap gap-2">
                    {member.skills.map((skill, idx) => (
                      <Badge key={idx} variant="outline" className="bg-[#247C75]/10 text-[#247C75] border-[#247C75]/30">
                        {skill}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-5 max-w-2xl bg-white shadow-sm">
          <TabsTrigger value="overview" className="gap-2">
            <CheckCircle2 className="h-4 w-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="tasks" className="gap-2">
            <FileText className="h-4 w-4" />
            Tasks ({tasks.length})
          </TabsTrigger>
          <TabsTrigger value="performance" className="gap-2">
            <TrendingUp className="h-4 w-4" />
            Performance
          </TabsTrigger>
          <TabsTrigger value="attendance" className="gap-2">
            <Calendar className="h-4 w-4" />
            Attendance
          </TabsTrigger>
          <TabsTrigger value="documents" className="gap-2">
            <FileText className="h-4 w-4" />
            Documents
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-6 space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="border-0 shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <CheckCircle2 className="h-8 w-8 text-blue-500" />
                  <TrendingUp className="h-4 w-4 text-green-600" />
                </div>
                <p className="text-sm text-slate-600 mb-1">Tasks Completed</p>
                <p className="text-3xl font-bold text-slate-900">{stats.tasksCompleted}</p>
                <p className="text-xs text-green-600 mt-1">+2% from last quarter</p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <Award className="h-8 w-8 text-amber-500" />
                  <TrendingUp className="h-4 w-4 text-green-600" />
                </div>
                <p className="text-sm text-slate-600 mb-1">Performance Rating</p>
                <p className="text-3xl font-bold text-slate-900">{stats.performanceRating}/5.0</p>
                <p className="text-xs text-green-600 mt-1">Exceeds expectations</p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <Calendar className="h-8 w-8 text-green-500" />
                  <TrendingUp className="h-4 w-4 text-green-600" />
                </div>
                <p className="text-sm text-slate-600 mb-1">Attendance Rate</p>
                <p className="text-3xl font-bold text-slate-900">{stats.attendanceRate}%</p>
                <p className="text-xs text-green-600 mt-1">Excellent attendance</p>
              </CardContent>
            </Card>
          </div>

          {/* Current Workload */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle>Current Workload</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-slate-600">Active Tasks</span>
                  <span className="font-semibold text-slate-900">{stats.inProgressTasks} / {totalTasks}</span>
                </div>
                <div className="w-full h-3 bg-slate-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-[#247C75] transition-all duration-300"
                    style={{ width: `${workloadPercentage}%` }}
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 mt-6">
                <div className="text-center p-4 bg-slate-50 rounded-lg">
                  <p className="text-3xl font-bold text-slate-900">{stats.openTasks}</p>
                  <p className="text-sm text-slate-600 mt-1">Open</p>
                </div>
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <p className="text-3xl font-bold text-blue-700">{stats.inProgressTasks}</p>
                  <p className="text-sm text-blue-600 mt-1">In Progress</p>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <p className="text-3xl font-bold text-green-700">{stats.completedTasks}</p>
                  <p className="text-sm text-green-600 mt-1">Completed</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tasks Tab */}
        <TabsContent value="tasks" className="mt-6">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle>Assigned Tasks</CardTitle>
            </CardHeader>
            <CardContent>
              {tasks.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <FileText className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p>No tasks assigned yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {tasks.map(task => (
                    <div key={task.id} className="p-4 rounded-lg border border-slate-200 hover:border-slate-300 transition-colors">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-semibold text-slate-900">{task.title}</h4>
                          {task.description && (
                            <p className="text-sm text-slate-600 mt-1">{task.description}</p>
                          )}
                          <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
                            <span>{task.estimated_hours}h</span>
                            {task.scheduled_date && (
                              <span>Due: {new Date(task.scheduled_date).toLocaleDateString()}</span>
                            )}
                          </div>
                        </div>
                        <Badge 
                          className={
                            task.status === 'completed' ? 'bg-green-100 text-green-700' :
                            task.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                            'bg-slate-100 text-slate-700'
                          }
                        >
                          {task.status.replace('_', ' ')}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Performance Tab */}
        <TabsContent value="performance" className="mt-6">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-[#247C75]" />
                Performance Metrics
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center py-12 text-slate-500">
                <TrendingUp className="h-16 w-16 mx-auto mb-4 text-slate-300" />
                <p className="text-lg font-medium mb-2">Performance Data Coming Soon</p>
                <p className="text-sm">Detailed performance analytics and trends</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Attendance Tab */}
        <TabsContent value="attendance" className="mt-6">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-[#247C75]" />
                Attendance Records
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center py-12 text-slate-500">
                <Calendar className="h-16 w-16 mx-auto mb-4 text-slate-300" />
                <p className="text-lg font-medium mb-2">Attendance Tracking Coming Soon</p>
                <p className="text-sm">Check-in/out history and patterns</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents" className="mt-6">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-[#247C75]" />
                Documents & Files
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center py-12 text-slate-500">
                <FileText className="h-16 w-16 mx-auto mb-4 text-slate-300" />
                <p className="text-lg font-medium mb-2">Document Management Coming Soon</p>
                <p className="text-sm">Contracts, certifications, and training materials</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default TeamMemberDetail;
